import {
  ClockSynchronizer,
  SharedGameClock,
  acceptStartProposal,
  createStartProposal,
  isExactStartAcceptance,
  monotonicNow,
  type ClockEstimate,
} from "./clock";
import {
  createSeedCommit,
  deriveSharedSeed,
  verifyNonceCommit,
  type SeedCommitRevealState,
} from "./crypto";
import {
  BrowserFeatureError,
  MatchTransportError,
  errorMessage,
} from "./errors";
import {
  NewestPoseFilter,
  PoseCadence,
  ReconnectJournal,
  ReliableSequenceTracker,
  ScheduledAttackInbox,
  SequenceAllocator,
  type ScheduledAttack,
} from "./journal";
import {
  DEFAULT_ATTACK_DELAY_TICKS,
  PROTOCOL_VERSION,
  channelForMessage,
  deserializeDataChannelEnvelope,
  isControlMessage,
  makeEnvelope,
  serializeEnvelope,
  type AttackMessage,
  type CheckpointMessage,
  type ControlEnvelope,
  type ControlMessage,
  type HeartbeatMessage,
  type HelloMessage,
  type InputBatchMessage,
  type LockCommitMessage,
  type MatchEnvelope,
  type PiecePoseMessage,
  type PoseChannelMessage,
  type ReadyMessage,
  type ReconnectHelloMessage,
  type RematchMessage,
  type ResultAckMessage,
  type ResumeMessage,
  type SeedRevealMessage,
  type TopOutMessage,
  type PeerCapabilities,
} from "./protocol";
import {
  SignalingClient,
  DEFAULT_STUN_SERVERS,
  fetchTurnCredentials,
  type SignalingAnswerMessage,
  type SignalingClientOptions,
  type SignalingIceMessage,
  type SignalingJoinedMessage,
  type SignalingOfferMessage,
  type SignalingPeerJoinedMessage,
  type SignalingPeerLeftMessage,
  type SignalingRenegotiateMessage,
  type SignalingResumeReservation,
  type SerializedIceCandidate,
  type TurnCredentials,
} from "./signaling";

export const CONTROL_CHANNEL_LABEL = "bwwk-control";
export const POSE_CHANNEL_LABEL = "bwwk-pose";

export type PeerMatchState =
  | "idle"
  | "connecting"
  | "negotiating"
  | "countdown"
  | "playing"
  | "result-pending"
  | "round-result"
  | "reconnecting"
  | "resyncing"
  | "aborted"
  | "closed";

export interface StartAgreement {
  readonly proposal: ReadyMessage;
  readonly localStartTimeMs: number;
  readonly clock: SharedGameClock;
}

interface ControlAckWaiter {
  readonly sequence: number;
  readonly resolve: () => void;
  readonly reject: (error: Error) => void;
  readonly timer: ReturnType<typeof setTimeout>;
}

export interface PeerMatchSessionCallbacks {
  readonly onStateChange?: (
    state: PeerMatchState,
    previousState: PeerMatchState,
  ) => void;
  readonly onSignalingJoined?: (message: SignalingJoinedMessage) => void;
  readonly onPeerJoined?: (message: SignalingPeerJoinedMessage) => void;
  readonly onPeerLeft?: (message: SignalingPeerLeftMessage) => void;
  readonly onPeerConnectionState?: (state: RTCPeerConnectionState) => void;
  readonly onChannelsOpen?: () => void;
  readonly onHandshakeProgress?: (
    stage: "hello" | "seed-reveal" | "clock" | "ready",
  ) => void;
  readonly onSeedReady?: (seedHex: string) => void;
  readonly onClockEstimate?: (estimate: ClockEstimate) => void;
  readonly onStartAgreed?: (agreement: StartAgreement) => void;
  readonly onInputBatch?: (
    message: InputBatchMessage,
    envelope: MatchEnvelope<InputBatchMessage>,
  ) => void;
  readonly onPose?: (
    message: PiecePoseMessage,
    envelope: MatchEnvelope<PiecePoseMessage>,
  ) => void;
  readonly onLockCommit?: (
    message: LockCommitMessage,
    envelope: MatchEnvelope<LockCommitMessage>,
  ) => void;
  readonly onAttack?: (
    attack: ScheduledAttack,
    envelope: MatchEnvelope<AttackMessage>,
  ) => void;
  readonly onCheckpoint?: (
    message: CheckpointMessage,
    envelope: MatchEnvelope<CheckpointMessage>,
  ) => void;
  readonly onTopOut?: (
    message: TopOutMessage,
    envelope: MatchEnvelope<TopOutMessage>,
  ) => void;
  readonly onResultAck?: (
    message: ResultAckMessage,
    envelope: MatchEnvelope<ResultAckMessage>,
  ) => void;
  readonly onReconnectHello?: (
    message: ReconnectHelloMessage,
    envelope: MatchEnvelope<ReconnectHelloMessage>,
  ) => void;
  readonly onResume?: (
    message: ResumeMessage,
    envelope: MatchEnvelope<ResumeMessage>,
  ) => void;
  readonly onRematch?: (
    message: RematchMessage,
    envelope: MatchEnvelope<RematchMessage>,
  ) => void;
  readonly onMessage?: (envelope: MatchEnvelope) => void;
  readonly onSequenceGap?: (expected: number, received: number) => void;
  readonly onProtocolError?: (error: Error) => void;
  readonly onError?: (error: Error) => void;
}

export interface PeerMatchSessionOptions {
  readonly matchId: string;
  readonly senderId: string;
  readonly rulesHash: string;
  readonly manifestHash: string;
  readonly signaling: SignalingClient | SignalingClientOptions;
  readonly rtcConfiguration?: RTCConfiguration;
  /** Worker origin used to mint short-lived ICE credentials after room join. */
  readonly turnCredentialOrigin?: string;
  readonly turnCredentialsProvider?: (
    joined: SignalingJoinedMessage,
  ) => Promise<TurnCredentials>;
  /** Also defaults true when the public page URL contains `?relay=1`. */
  readonly relayOnly?: boolean;
  readonly capabilities?: Partial<PeerCapabilities>;
  readonly callbacks?: PeerMatchSessionCallbacks;
  readonly now?: () => number;
  readonly tickProvider?: () => number;
  readonly clockSampleCount?: number;
  readonly startLeadTimeMs?: number;
  readonly journalRetentionMs?: number;
  readonly RTCPeerConnectionClass?: typeof RTCPeerConnection;
}

const DEFAULT_CAPABILITIES: PeerCapabilities = {
  poseChannel: true,
  checkpoints: true,
  reconnect: true,
  seedCommitReveal: true,
};

function normalizeError(error: unknown): Error {
  return error instanceof Error ? error : new Error(errorMessage(error));
}

function rtcCandidate(candidate: RTCIceCandidate): SerializedIceCandidate {
  if (typeof candidate.toJSON === "function") {
    const serialized = candidate.toJSON();
    return { ...serialized, candidate: serialized.candidate ?? "" };
  }
  return {
    candidate: candidate.candidate,
    sdpMid: candidate.sdpMid,
    sdpMLineIndex: candidate.sdpMLineIndex,
    usernameFragment: candidate.usernameFragment,
  };
}

/**
 * Owns signaling, one RTCPeerConnection, the reliable control channel, and the
 * unordered/no-retransmit pose channel. Game simulation remains fully local;
 * callbacks only deliver remote observations and fixed-state commits.
 */
export class PeerMatchSession {
  readonly matchId: string;
  readonly senderId: string;
  readonly rulesHash: string;
  readonly manifestHash: string;
  readonly journal: ReconnectJournal;

  private readonly options: PeerMatchSessionOptions;
  private callbacks: PeerMatchSessionCallbacks;
  private readonly signaling: SignalingClient;
  private readonly now: () => number;
  private readonly tickProvider?: () => number;
  private readonly clockSync: ClockSynchronizer;
  private readonly controlSequences = new SequenceAllocator();
  private readonly poseSequences = new SequenceAllocator();
  private readonly inboundControl = new ReliableSequenceTracker();
  private readonly poseFilter = new NewestPoseFilter();
  private readonly poseCadence = new PoseCadence();
  private readonly attackInbox = new ScheduledAttackInbox();

  private currentState: PeerMatchState = "idle";
  private peerConnection?: RTCPeerConnection;
  private resolvedRtcConfiguration?: RTCConfiguration;
  private connectionPrepared = false;
  private controlChannel?: RTCDataChannel;
  private poseChannel?: RTCDataChannel;
  private joined?: SignalingJoinedMessage;
  private remoteSignalPeerId?: string;
  private remoteSenderId?: string;
  private revision = 0;
  private makingOffer = false;
  private pendingIce: SignalingIceMessage[] = [];
  private pendingOffer?: SignalingOfferMessage;
  private pendingAnswer?: SignalingAnswerMessage;
  private pendingRenegotiate?: SignalingRenegotiateMessage;
  private channelsWereOpen = false;
  private localSeed?: SeedCommitRevealState;
  private remoteHello?: MatchEnvelope<HelloMessage>;
  private remoteNonce?: string;
  private sharedSeedHex?: string;
  private helloSent = false;
  private revealSent = false;
  private clockTimer?: ReturnType<typeof setInterval>;
  private clockAttempts = 0;
  private heartbeatTimer?: ReturnType<typeof setInterval>;
  private startTimer?: ReturnType<typeof setTimeout>;
  private proposedStart?: ReadyMessage;
  private pendingRemoteStart?: MatchEnvelope<ReadyMessage>;
  private activeClock?: SharedGameClock;
  private isClosed = false;
  private highestRemoteControlAck = 0;
  private lastRematchRequestSeq?: number;
  private readonly controlAckWaiters = new Set<ControlAckWaiter>();

  constructor(options: PeerMatchSessionOptions) {
    if (!options.matchId || !options.senderId || !options.rulesHash || !options.manifestHash) {
      throw new MatchTransportError(
        "invalid-state",
        "matchId, senderId, rulesHash, and manifestHash are required",
      );
    }
    this.options = options;
    this.matchId = options.matchId;
    this.senderId = options.senderId;
    this.rulesHash = options.rulesHash;
    this.manifestHash = options.manifestHash;
    this.callbacks = options.callbacks ?? {};
    this.now = options.now ?? monotonicNow;
    this.tickProvider = options.tickProvider;
    this.clockSync = new ClockSynchronizer({
      targetSampleCount: options.clockSampleCount,
      now: this.now,
    });
    this.journal = new ReconnectJournal(options.journalRetentionMs);
    this.signaling =
      options.signaling instanceof SignalingClient
        ? options.signaling
        : new SignalingClient(options.signaling);
    this.installSignalingCallbacks();
  }

  get state(): PeerMatchState {
    return this.currentState;
  }

  get gameClock(): SharedGameClock | undefined {
    return this.activeClock;
  }

  get clockEstimate(): ClockEstimate | undefined {
    try {
      return this.clockSync.estimate();
    } catch {
      return undefined;
    }
  }

  get seedHex(): string | undefined {
    return this.sharedSeedHex;
  }

  get remoteId(): string | undefined {
    return this.remoteSenderId;
  }

  get seat(): 1 | 2 | undefined {
    return this.joined?.seat;
  }

  get roomCode(): string | undefined {
    return this.joined?.roomCode;
  }

  get resumeToken(): string | undefined {
    return this.joined?.resumeToken;
  }

  get startTick(): number | undefined {
    return this.activeClock?.startTick;
  }

  setCallbacks(callbacks: PeerMatchSessionCallbacks): void {
    this.callbacks = callbacks;
  }

  async connect(): Promise<SignalingJoinedMessage> {
    if (this.currentState !== "idle") {
      throw new MatchTransportError("invalid-state", "Session has already been started");
    }
    this.setState("connecting");
    try {
      this.localSeed = await createSeedCommit(this.matchId, this.senderId);
      const joined = await this.signaling.join();
      await this.preparePeerConnection(joined);
      return joined;
    } catch (error) {
      this.signaling.leave(4001, "connect failed");
      this.reportError(error, false);
      this.setState("aborted");
      throw error;
    }
  }

  private installSignalingCallbacks(): void {
    this.signaling.setCallbacks({
      onJoined: (message) => this.handleSignalingJoined(message),
      onPeerJoined: (message) => {
        this.remoteSignalPeerId = message.peerId;
        this.callbacks.onPeerJoined?.(message);
        if (this.connectionPrepared && this.joined?.seat === 1) {
          void this.createOffer().catch((error) => this.reportError(error));
        }
      },
      onPeerLeft: (message) => this.handlePeerLeft(message),
      onOffer: (message) => void this.handleOffer(message).catch((error) => this.reportError(error)),
      onAnswer: (message) => void this.handleAnswer(message).catch((error) => this.reportError(error)),
      onIce: (message) => void this.handleIce(message).catch((error) => this.reportError(error)),
      onRenegotiate: (message) =>
        void this.handleRenegotiate(message).catch((error) => this.reportError(error)),
      onServerError: (message) =>
        this.reportError(
          new MatchTransportError("signaling", message.message, {
            recoverable: message.recoverable,
          }),
          message.recoverable,
        ),
      onClose: () => {
        if (!this.isClosed && this.currentState !== "aborted") this.setState("reconnecting");
      },
      onError: (error) => this.reportError(error),
    });
  }

  private handleSignalingJoined(message: SignalingJoinedMessage): void {
    this.joined = message;
    this.callbacks.onSignalingJoined?.(message);
    const other = message.peers.find((peer) => peer.peerId !== message.peerId);
    if (other) this.remoteSignalPeerId = other.peerId;
  }

  private handlePeerLeft(message: SignalingPeerLeftMessage): void {
    this.callbacks.onPeerLeft?.(message);
    if (message.final) {
      this.setState("aborted");
    } else {
      this.setState("reconnecting");
    }
  }

  private ensurePeerConnection(): RTCPeerConnection {
    if (this.peerConnection) return this.peerConnection;
    if (!this.connectionPrepared) {
      throw new MatchTransportError(
        "invalid-state",
        "ICE configuration must be prepared after signaling join",
      );
    }
    const RTCPeerConnectionClass =
      this.options.RTCPeerConnectionClass ?? globalThis.RTCPeerConnection;
    if (typeof RTCPeerConnectionClass === "undefined") {
      throw new BrowserFeatureError(
        "WebRTC",
        "RTCPeerConnection requires a modern secure-context browser",
      );
    }
    const connection = new RTCPeerConnectionClass(this.resolvedRtcConfiguration);
    this.peerConnection = connection;
    connection.addEventListener("icecandidate", (event) => {
      try {
        this.signaling.sendIce(
          event.candidate ? rtcCandidate(event.candidate) : null,
          this.revision,
          this.remoteSignalPeerId,
        );
      } catch (error) {
        this.reportError(error);
      }
    });
    connection.addEventListener("datachannel", (event) => {
      try {
        this.attachDataChannel(event.channel);
      } catch (error) {
        this.reportError(error, false);
      }
    });
    connection.addEventListener("connectionstatechange", () => {
      this.callbacks.onPeerConnectionState?.(connection.connectionState);
      switch (connection.connectionState) {
        case "connected":
          if (
            this.currentState === "connecting" ||
            this.currentState === "reconnecting" ||
            this.currentState === "resyncing"
          ) {
            this.setState("negotiating");
          }
          break;
        case "disconnected":
        case "failed":
          if (!this.isClosed) this.setState("reconnecting");
          break;
        case "closed":
          if (!this.isClosed) this.setState("closed");
          break;
      }
    });
    connection.addEventListener("negotiationneeded", () => {
      if (this.joined?.seat === 1 && this.remoteSignalPeerId) {
        void this.createOffer().catch((error) => this.reportError(error));
      }
    });
    return connection;
  }

  private async preparePeerConnection(joined: SignalingJoinedMessage): Promise<void> {
    let credentials: TurnCredentials | undefined;
    if (this.options.turnCredentialsProvider) {
      credentials = await this.options.turnCredentialsProvider(joined);
    } else if (this.options.turnCredentialOrigin) {
      credentials = await fetchTurnCredentials(this.options.turnCredentialOrigin, {
        roomCode: joined.roomCode,
        resumeToken: joined.resumeToken,
      });
    }

    const configuredServers = this.options.rtcConfiguration?.iceServers;
    const iceServers = credentials?.configured
      ? [...credentials.iceServers]
      : configuredServers && configuredServers.length > 0
        ? [...configuredServers]
        : [...DEFAULT_STUN_SERVERS];
    const relayOnly = this.options.relayOnly ?? this.urlRequestsRelay();
    if (relayOnly && !this.hasTurnServer(iceServers)) {
      throw new MatchTransportError(
        "peer-connection",
        "Relay-only mode was requested, but no TURN server is configured",
        { recoverable: true },
      );
    }
    this.resolvedRtcConfiguration = {
      ...this.options.rtcConfiguration,
      iceServers,
      ...(relayOnly ? { iceTransportPolicy: "relay" as const } : {}),
    };
    this.connectionPrepared = true;
    this.ensurePeerConnection();

    if (joined.seat === 1) {
      this.createLocalChannels();
      if (this.remoteSignalPeerId) await this.createOffer();
    } else if (this.pendingOffer) {
      const offer = this.pendingOffer;
      this.pendingOffer = undefined;
      await this.handleOffer(offer);
    }
    if (this.pendingAnswer) {
      const answer = this.pendingAnswer;
      this.pendingAnswer = undefined;
      await this.handleAnswer(answer);
    }
    if (this.pendingRenegotiate) {
      const renegotiate = this.pendingRenegotiate;
      this.pendingRenegotiate = undefined;
      await this.handleRenegotiate(renegotiate);
    }
  }

  private urlRequestsRelay(): boolean {
    if (typeof location === "undefined") return false;
    try {
      return new URL(location.href).searchParams.get("relay") === "1";
    } catch {
      return false;
    }
  }

  private hasTurnServer(servers: readonly RTCIceServer[]): boolean {
    return servers.some((server) => {
      const urls = typeof server.urls === "string" ? [server.urls] : server.urls;
      return urls.some((url) => /^turns?:/i.test(url));
    });
  }

  private createLocalChannels(): void {
    if (this.controlChannel || this.poseChannel) return;
    const connection = this.ensurePeerConnection();
    this.attachDataChannel(
      connection.createDataChannel(CONTROL_CHANNEL_LABEL, { ordered: true }),
    );
    this.attachDataChannel(
      connection.createDataChannel(POSE_CHANNEL_LABEL, {
        ordered: false,
        maxRetransmits: 0,
      }),
    );
  }

  private attachDataChannel(channel: RTCDataChannel): void {
    if (channel.label === CONTROL_CHANNEL_LABEL) {
      if (!channel.ordered) {
        throw new MatchTransportError("protocol", "Control channel must be ordered");
      }
      this.controlChannel = channel;
    } else if (channel.label === POSE_CHANNEL_LABEL) {
      if (channel.ordered || channel.maxRetransmits !== 0) {
        throw new MatchTransportError(
          "protocol",
          "Pose channel must be unordered with maxRetransmits=0",
        );
      }
      this.poseChannel = channel;
    } else {
      channel.close();
      throw new MatchTransportError("protocol", `Unknown data channel: ${channel.label}`);
    }
    channel.binaryType = "arraybuffer";
    channel.addEventListener("open", () => this.handleChannelOpen());
    channel.addEventListener("close", () => {
      this.channelsWereOpen = false;
      this.rejectControlAckWaiters(
        new MatchTransportError(
          "channel-closed",
          "Control acknowledgement was interrupted",
          { recoverable: true },
        ),
      );
      if (!this.isClosed) this.setState("reconnecting");
    });
    channel.addEventListener("error", () =>
      this.reportError(
        new MatchTransportError(
          "channel-closed",
          `${channel.label} data channel failed`,
          { recoverable: true },
        ),
      ),
    );
    const kind = channel.label === CONTROL_CHANNEL_LABEL ? "control" : "pose";
    channel.addEventListener("message", (event) => {
      void this.handleDataMessage(kind, event.data).catch((error) =>
        this.reportProtocolError(error),
      );
    });
    if (channel.readyState === "open") this.handleChannelOpen();
  }

  private handleChannelOpen(): void {
    if (
      this.channelsWereOpen ||
      this.controlChannel?.readyState !== "open" ||
      this.poseChannel?.readyState !== "open"
    ) {
      return;
    }
    this.channelsWereOpen = true;
    this.setState("negotiating");
    this.callbacks.onChannelsOpen?.();
    this.sendHello();
    this.startClockSampling();
    this.startHeartbeat();
  }

  private async createOffer(options: { readonly iceRestart?: boolean } = {}): Promise<void> {
    const connection = this.ensurePeerConnection();
    if (
      this.makingOffer ||
      this.joined?.seat !== 1 ||
      !this.remoteSignalPeerId ||
      connection.signalingState !== "stable"
    ) {
      return;
    }
    this.createLocalChannels();
    this.makingOffer = true;
    this.revision += 1;
    try {
      const offer = await connection.createOffer({ iceRestart: options.iceRestart ?? false });
      await connection.setLocalDescription(offer);
      if (!connection.localDescription?.sdp) {
        throw new MatchTransportError("peer-connection", "Offer SDP was not created");
      }
      this.signaling.sendOffer(
        connection.localDescription.sdp,
        this.revision,
        this.remoteSignalPeerId,
      );
    } finally {
      this.makingOffer = false;
    }
  }

  private async handleOffer(message: SignalingOfferMessage): Promise<void> {
    if (!this.connectionPrepared) {
      this.pendingOffer = message;
      return;
    }
    if (message.revision < this.revision) return;
    this.remoteSignalPeerId = message.fromPeerId;
    this.revision = message.revision;
    const connection = this.ensurePeerConnection();
    if (connection.signalingState !== "stable") {
      await connection.setLocalDescription({ type: "rollback" });
    }
    await connection.setRemoteDescription({ type: "offer", sdp: message.sdp });
    await this.flushPendingIce();
    const answer = await connection.createAnswer();
    await connection.setLocalDescription(answer);
    if (!connection.localDescription?.sdp) {
      throw new MatchTransportError("peer-connection", "Answer SDP was not created");
    }
    this.signaling.sendAnswer(
      connection.localDescription.sdp,
      this.revision,
      message.fromPeerId,
    );
  }

  private async handleAnswer(message: SignalingAnswerMessage): Promise<void> {
    if (!this.connectionPrepared) {
      this.pendingAnswer = message;
      return;
    }
    if (message.revision !== this.revision) return;
    this.remoteSignalPeerId = message.fromPeerId;
    const connection = this.ensurePeerConnection();
    await connection.setRemoteDescription({ type: "answer", sdp: message.sdp });
    await this.flushPendingIce();
  }

  private async handleIce(message: SignalingIceMessage): Promise<void> {
    if (message.revision < this.revision) return;
    if (!this.connectionPrepared) {
      this.pendingIce.push(message);
      return;
    }
    const connection = this.ensurePeerConnection();
    if (message.revision > this.revision || !connection.remoteDescription) {
      this.pendingIce.push(message);
      return;
    }
    await connection.addIceCandidate(message.candidate);
  }

  private async flushPendingIce(): Promise<void> {
    const connection = this.ensurePeerConnection();
    const applicable = this.pendingIce.filter((message) => message.revision === this.revision);
    this.pendingIce = this.pendingIce.filter((message) => message.revision > this.revision);
    for (const message of applicable) await connection.addIceCandidate(message.candidate);
  }

  private async handleRenegotiate(message: SignalingRenegotiateMessage): Promise<void> {
    this.remoteSignalPeerId = message.fromPeerId;
    if (!this.connectionPrepared) {
      this.pendingRenegotiate = message;
      return;
    }
    if (this.joined?.seat === 1) {
      await this.createOffer({ iceRestart: message.reason === "ice-restart" });
    }
  }

  async attemptIceRestart(): Promise<void> {
    if (!this.remoteSignalPeerId) {
      throw new MatchTransportError("peer-connection", "No remote peer to reconnect", {
        recoverable: true,
      });
    }
    this.setState("reconnecting");
    if (this.joined?.seat === 1) {
      await this.createOffer({ iceRestart: true });
    } else {
      this.signaling.requestRenegotiation(
        this.revision,
        "ice-restart",
        this.remoteSignalPeerId,
      );
    }
  }

  private currentTick(): number {
    const tick = this.tickProvider?.() ?? this.activeClock?.nowTick() ?? 0;
    return Number.isSafeInteger(tick) && tick >= 0 ? tick : 0;
  }

  private sendHello(): void {
    if (this.helloSent || !this.localSeed) return;
    const capabilities = Object.fromEntries(
      Object.entries({
        ...DEFAULT_CAPABILITIES,
        ...this.options.capabilities,
      }).filter((entry): entry is [string, boolean | number | string] =>
        entry[1] !== undefined,
      ),
    ) as PeerCapabilities;
    this.sendControlMessage(
      {
        type: "hello",
        rulesHash: this.rulesHash,
        capabilities,
        nonceCommit: this.localSeed.commit,
      },
      0,
    );
    this.helloSent = true;
    this.callbacks.onHandshakeProgress?.("hello");
  }

  private sendReveal(): void {
    if (this.revealSent || !this.localSeed || !this.remoteHello) return;
    this.sendControlMessage({ type: "seed-reveal", nonce: this.localSeed.nonce }, 0);
    this.revealSent = true;
    this.callbacks.onHandshakeProgress?.("seed-reveal");
  }

  private startClockSampling(): void {
    if (this.clockTimer || this.clockSync.complete) return;
    const sendSample = (): void => {
      if (this.poseChannel?.readyState !== "open") return;
      if (this.clockSync.complete) {
        this.finishClockSampling();
        return;
      }
      if (this.clockAttempts >= this.clockSync.targetSampleCount * 3) {
        this.stopClockTimer();
        if (this.clockSync.sampleCount >= 5) {
          this.callbacks.onClockEstimate?.(this.clockSync.estimate());
        }
        this.reportError(
          new MatchTransportError(
            "clock",
            `Clock sampling failed (${this.clockSync.sampleCount}/${this.clockSync.targetSampleCount})`,
            { recoverable: true },
          ),
        );
        return;
      }
      this.clockAttempts += 1;
      this.sendPoseMessage(this.clockSync.createPing(), this.currentTick());
    };
    sendSample();
    this.clockTimer = setInterval(sendSample, 100);
  }

  private stopClockTimer(): void {
    if (this.clockTimer !== undefined) clearInterval(this.clockTimer);
    this.clockTimer = undefined;
  }

  private finishClockSampling(): void {
    this.stopClockTimer();
    const estimate = this.clockSync.estimate();
    this.callbacks.onClockEstimate?.(estimate);
    this.callbacks.onHandshakeProgress?.("clock");
    this.maybeNegotiateStart();
  }

  private maybeNegotiateStart(): void {
    if (!this.sharedSeedHex || !this.clockSync.complete) return;
    if (this.joined?.seat === 1 && !this.proposedStart) {
      const proposal = createStartProposal({
        coordinatorId: this.senderId,
        manifestHash: this.manifestHash,
        nowMs: this.now(),
        leadTimeMs: this.options.startLeadTimeMs,
      });
      this.proposedStart = proposal;
      this.sendControlMessage(proposal, proposal.startTick);
      this.callbacks.onHandshakeProgress?.("ready");
    } else if (this.pendingRemoteStart) {
      const pending = this.pendingRemoteStart;
      this.pendingRemoteStart = undefined;
      this.handleReady(pending);
    }
  }

  private establishStart(proposal: ReadyMessage, localStartTimeMs: number): void {
    if (this.activeClock) return;
    const clock = new SharedGameClock({
      localStartTimeMs,
      startTick: proposal.startTick,
      now: this.now,
    });
    this.activeClock = clock;
    this.setState("countdown");
    const agreement: StartAgreement = { proposal, localStartTimeMs, clock };
    this.callbacks.onStartAgreed?.(agreement);
    const delay = Math.max(0, localStartTimeMs - this.now());
    this.startTimer = setTimeout(() => {
      this.startTimer = undefined;
      if (!this.isClosed && this.currentState === "countdown") this.setState("playing");
    }, Math.min(delay, 2_147_483_647));
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) return;
    this.heartbeatTimer = setInterval(() => {
      if (this.controlChannel?.readyState !== "open") return;
      try {
        const heartbeat: HeartbeatMessage = { type: "heartbeat", sentAtMs: this.now() };
        this.sendControlMessage(heartbeat, this.currentTick());
        this.journal.prune(this.now());
      } catch (error) {
        this.reportError(error);
      }
    }, 1_000);
  }

  private async handleDataMessage(
    channel: "control" | "pose",
    data: unknown,
  ): Promise<void> {
    const envelope = await deserializeDataChannelEnvelope(data);
    if (envelope.matchId !== this.matchId || envelope.senderId === this.senderId) {
      throw new MatchTransportError("protocol", "Envelope is for a different match or sender");
    }
    if (channelForMessage(envelope.message) !== channel) {
      throw new MatchTransportError("protocol", "Message arrived on the wrong data channel");
    }
    if (this.remoteSenderId && envelope.senderId !== this.remoteSenderId) {
      throw new MatchTransportError("protocol", "Remote sender identity changed");
    }
    this.remoteSenderId ??= envelope.senderId;
    this.journal.acknowledgeThrough(envelope.ack, this.now());
    this.noteRemoteControlAck(envelope.ack);
    this.callbacks.onMessage?.(envelope);

    if (channel === "control") {
      const observation = this.inboundControl.observe(envelope.seq);
      if (observation.duplicate) return;
      if (observation.gap) {
        this.callbacks.onSequenceGap?.(observation.previous + 1, envelope.seq);
      }
      await this.dispatchControl(envelope as ControlEnvelope);
    } else {
      this.dispatchPose(envelope as MatchEnvelope<PoseChannelMessage>);
    }
  }

  private async dispatchControl(envelope: ControlEnvelope): Promise<void> {
    const message = envelope.message;
    switch (message.type) {
      case "hello":
        await this.handleHello(envelope as MatchEnvelope<HelloMessage>);
        break;
      case "seed-reveal":
        await this.handleSeedReveal(envelope as MatchEnvelope<SeedRevealMessage>);
        break;
      case "ready":
        this.handleReady(envelope as MatchEnvelope<ReadyMessage>);
        break;
      case "input-batch":
        this.callbacks.onInputBatch?.(
          message,
          envelope as MatchEnvelope<InputBatchMessage>,
        );
        break;
      case "lock-commit":
        this.callbacks.onLockCommit?.(
          message,
          envelope as MatchEnvelope<LockCommitMessage>,
        );
        break;
      case "attack": {
        const typed = envelope as MatchEnvelope<AttackMessage>;
        const scheduled = this.attackInbox.receive(typed);
        if (scheduled) this.callbacks.onAttack?.(scheduled, typed);
        break;
      }
      case "checkpoint": {
        const typed = envelope as MatchEnvelope<CheckpointMessage>;
        this.journal.recordIncomingCheckpoint(typed, this.now());
        this.callbacks.onCheckpoint?.(message, typed);
        break;
      }
      case "top-out":
        this.setState("result-pending");
        this.callbacks.onTopOut?.(message, envelope as MatchEnvelope<TopOutMessage>);
        break;
      case "result-ack":
        this.setState("round-result");
        this.callbacks.onResultAck?.(
          message,
          envelope as MatchEnvelope<ResultAckMessage>,
        );
        break;
      case "reconnect-hello": {
        const typed = envelope as MatchEnvelope<ReconnectHelloMessage>;
        this.setState("resyncing");
        this.callbacks.onReconnectHello?.(message, typed);
        for (const missed of this.journal.replayAfter(message.lastSeq)) {
          this.sendExistingEnvelope(missed);
        }
        break;
      }
      case "resume":
        this.setState("resyncing");
        this.callbacks.onResume?.(message, envelope as MatchEnvelope<ResumeMessage>);
        break;
      case "rematch":
        this.callbacks.onRematch?.(message, envelope as MatchEnvelope<RematchMessage>);
        break;
      case "heartbeat":
        break;
    }
  }

  private dispatchPose(envelope: MatchEnvelope<PoseChannelMessage>): void {
    const message = envelope.message;
    switch (message.type) {
      case "piece-pose": {
        const typed = envelope as MatchEnvelope<PiecePoseMessage>;
        if (this.poseFilter.accept(typed)) this.callbacks.onPose?.(message, typed);
        break;
      }
      case "ping":
        this.sendPoseMessage(this.clockSync.createPong(message), this.currentTick());
        break;
      case "pong":
        if (this.clockSync.acceptPong(message) && this.clockSync.complete) {
          this.finishClockSampling();
        }
        break;
    }
  }

  private async handleHello(envelope: MatchEnvelope<HelloMessage>): Promise<void> {
    if (envelope.message.rulesHash !== this.rulesHash) {
      throw new MatchTransportError("handshake", "Peer uses a different rules hash");
    }
    if (
      !envelope.message.capabilities.poseChannel ||
      !envelope.message.capabilities.seedCommitReveal
    ) {
      throw new MatchTransportError("handshake", "Peer lacks required match capabilities");
    }
    if (this.remoteHello && this.remoteHello.message.nonceCommit !== envelope.message.nonceCommit) {
      throw new MatchTransportError("handshake", "Peer changed its seed commitment");
    }
    this.remoteHello = envelope;
    this.sendHello();
    this.sendReveal();
  }

  private async handleSeedReveal(
    envelope: MatchEnvelope<SeedRevealMessage>,
  ): Promise<void> {
    if (!this.remoteHello || !this.localSeed) {
      throw new MatchTransportError("handshake", "Seed reveal arrived before Hello");
    }
    const valid = await verifyNonceCommit(
      envelope.message.nonce,
      this.remoteHello.message.nonceCommit,
      `${this.matchId}:${envelope.senderId}`,
    );
    if (!valid) throw new MatchTransportError("seed", "Peer seed reveal did not match commit");
    if (this.remoteNonce && this.remoteNonce !== envelope.message.nonce) {
      throw new MatchTransportError("seed", "Peer changed its seed reveal");
    }
    this.remoteNonce = envelope.message.nonce;
    if (!this.sharedSeedHex) {
      this.sharedSeedHex = await deriveSharedSeed(this.matchId, [
        { senderId: this.senderId, nonce: this.localSeed.nonce },
        { senderId: envelope.senderId, nonce: envelope.message.nonce },
      ]);
      this.callbacks.onSeedReady?.(this.sharedSeedHex);
      this.maybeNegotiateStart();
    }
  }

  private handleReady(envelope: MatchEnvelope<ReadyMessage>): void {
    const message = envelope.message;
    if (!this.sharedSeedHex || !this.clockSync.complete) {
      this.pendingRemoteStart = envelope;
      return;
    }
    if (message.agreement === "propose") {
      if (this.joined?.seat !== 2 || message.coordinatorId !== envelope.senderId) {
        throw new MatchTransportError("handshake", "Only seat 1 may coordinate match start");
      }
      const accepted = acceptStartProposal({
        proposal: message,
        expectedManifestHash: this.manifestHash,
        remoteCoordinatorId: envelope.senderId,
        estimate: this.clockSync.estimate(),
        localNowMs: this.now(),
      });
      this.sendControlMessage(accepted.response, accepted.response.startTick);
      this.callbacks.onHandshakeProgress?.("ready");
      this.establishStart(message, accepted.localStartTimeMs);
    } else {
      if (
        this.joined?.seat !== 1 ||
        !this.proposedStart ||
        !isExactStartAcceptance(this.proposedStart, message)
      ) {
        throw new MatchTransportError("handshake", "Invalid match start acceptance");
      }
      this.establishStart(this.proposedStart, this.proposedStart.startTimeMs);
    }
  }

  sendControlMessage<M extends ControlMessage>(message: M, tick = this.currentTick()): MatchEnvelope<M> {
    if (!isControlMessage(message)) {
      throw new MatchTransportError("protocol", "Message does not belong on control channel");
    }
    if (this.controlChannel?.readyState !== "open") {
      throw new MatchTransportError("channel-closed", "Control data channel is not open", {
        recoverable: true,
      });
    }
    const envelope = makeEnvelope({
      matchId: this.matchId,
      senderId: this.senderId,
      seq: this.controlSequences.next(),
      ack: this.inboundControl.highestReceived,
      tick,
      message,
    });
    this.controlChannel.send(serializeEnvelope(envelope));
    this.journal.recordOutgoing(envelope as ControlEnvelope, this.now());
    return envelope;
  }

  private sendPoseMessage<M extends PoseChannelMessage>(
    message: M,
    tick = this.currentTick(),
  ): MatchEnvelope<M> {
    if (this.poseChannel?.readyState !== "open") {
      throw new MatchTransportError("channel-closed", "Pose data channel is not open", {
        recoverable: true,
      });
    }
    const envelope = makeEnvelope({
      matchId: this.matchId,
      senderId: this.senderId,
      seq: this.poseSequences.next(),
      ack: this.inboundControl.highestReceived,
      tick,
      message,
    });
    this.poseChannel.send(serializeEnvelope(envelope));
    return envelope;
  }

  private sendExistingEnvelope(envelope: ControlEnvelope): void {
    if (this.controlChannel?.readyState !== "open") {
      throw new MatchTransportError("channel-closed", "Cannot replay while control is closed", {
        recoverable: true,
      });
    }
    this.controlChannel.send(serializeEnvelope(envelope));
  }

  sendInputBatch(message: Omit<InputBatchMessage, "type">): MatchEnvelope<InputBatchMessage> {
    return this.sendControlMessage({ type: "input-batch", ...message }, message.firstTick);
  }

  /** Returns false between 18-tick cadence boundaries. */
  sendPose(message: Omit<PiecePoseMessage, "type">, force = false): boolean {
    if (!this.poseCadence.shouldSend(message.poseTick, force)) return false;
    this.sendPoseMessage({ type: "piece-pose", ...message }, message.poseTick);
    return true;
  }

  sendLockCommit(message: Omit<LockCommitMessage, "type">): MatchEnvelope<LockCommitMessage> {
    return this.sendControlMessage({ type: "lock-commit", ...message }, message.lockTick);
  }

  sendAttack(
    message: Omit<AttackMessage, "type" | "attackId" | "eligibleTick"> & {
      readonly eligibleTick?: number;
    },
  ): MatchEnvelope<AttackMessage> {
    const eligibleTick = message.eligibleTick ?? message.createdTick + DEFAULT_ATTACK_DELAY_TICKS;
    return this.sendControlMessage(
      {
        type: "attack",
        attackId: `${this.senderId}:${message.sourceLockSeq}`,
        sourceLockSeq: message.sourceLockSeq,
        createdTick: message.createdTick,
        eligibleTick,
        lines: message.lines,
      },
      message.createdTick,
    );
  }

  /** Engine calls this at its own post-lock garbage boundary. */
  takeEligibleAttacksAtLock(lockTick: number): readonly ScheduledAttack[] {
    return this.attackInbox.takeForLockBoundary(lockTick);
  }

  sendCheckpoint(message: Omit<CheckpointMessage, "type">): MatchEnvelope<CheckpointMessage> {
    return this.sendControlMessage(
      { type: "checkpoint", ...message },
      message.checkpointTick,
    );
  }

  confirmCheckpoint(senderId: string, stateHash: string): boolean {
    return this.journal.markCheckpointHashMatched(senderId, stateHash);
  }

  sendTopOut(message: Omit<TopOutMessage, "type">): MatchEnvelope<TopOutMessage> {
    this.setState("result-pending");
    return this.sendControlMessage({ type: "top-out", ...message }, message.topOutTick);
  }

  sendResultAck(message: Omit<ResultAckMessage, "type">): MatchEnvelope<ResultAckMessage> {
    this.setState("round-result");
    return this.sendControlMessage({ type: "result-ack", ...message }, message.topOutTick);
  }

  requestRematch(round: number, proposedMatchId?: string): MatchEnvelope<RematchMessage> {
    const envelope = this.sendControlMessage({
      type: "rematch",
      action: "request",
      round,
      ...(proposedMatchId ? { proposedMatchId } : {}),
    });
    this.lastRematchRequestSeq = envelope.seq;
    return envelope;
  }

  respondToRematch(
    action: "accept" | "decline",
    round: number,
    proposedMatchId?: string,
  ): MatchEnvelope<RematchMessage> {
    return this.sendControlMessage({
      type: "rematch",
      action,
      round,
      ...(proposedMatchId ? { proposedMatchId } : {}),
    });
  }

  sendReconnectHello(message?: Partial<Omit<ReconnectHelloMessage, "type">>): MatchEnvelope<ReconnectHelloMessage> {
    const checkpoint = this.journal.latestMatchedCheckpoint();
    return this.sendControlMessage({
      type: "reconnect-hello",
      lastSeq: message?.lastSeq ?? this.inboundControl.highestReceived,
      lastConfirmedTick:
        message?.lastConfirmedTick ?? checkpoint?.message.checkpointTick ?? 0,
      checkpointHash: message?.checkpointHash ?? checkpoint?.message.stateHash ?? "none",
    });
  }

  sendResume(message: Omit<ResumeMessage, "type">): MatchEnvelope<ResumeMessage> {
    return this.sendControlMessage({ type: "resume", ...message }, message.resumeTick);
  }

  private noteRemoteControlAck(sequence: number): void {
    if (sequence <= this.highestRemoteControlAck) return;
    this.highestRemoteControlAck = sequence;
    for (const waiter of [...this.controlAckWaiters]) {
      if (sequence < waiter.sequence) continue;
      clearTimeout(waiter.timer);
      this.controlAckWaiters.delete(waiter);
      waiter.resolve();
    }
  }

  private rejectControlAckWaiters(error: Error): void {
    for (const waiter of this.controlAckWaiters) {
      clearTimeout(waiter.timer);
      waiter.reject(error);
    }
    this.controlAckWaiters.clear();
  }

  waitForControlAcknowledgement(sequence: number, timeoutMs = 2_000): Promise<void> {
    if (!Number.isSafeInteger(sequence) || sequence < 1) {
      return Promise.reject(new RangeError("Acknowledgement sequence must be positive"));
    }
    if (this.highestRemoteControlAck >= sequence) return Promise.resolve();
    if (this.controlChannel?.readyState !== "open") {
      return Promise.reject(
        new MatchTransportError(
          "channel-closed",
          "Cannot wait for acknowledgement while control is closed",
          { recoverable: true },
        ),
      );
    }
    return new Promise<void>((resolve, reject) => {
      const waiter = {} as ControlAckWaiter;
      const timer = setTimeout(() => {
        this.controlAckWaiters.delete(waiter);
        reject(
          new MatchTransportError(
            "channel-closed",
            `Timed out waiting for control acknowledgement ${sequence}`,
            { recoverable: true },
          ),
        );
      }, timeoutMs);
      Object.assign(waiter, { sequence, resolve, reject, timer });
      this.controlAckWaiters.add(waiter);
      // The acknowledgement may have arrived between the checks above.
      if (this.highestRemoteControlAck >= sequence) {
        clearTimeout(timer);
        this.controlAckWaiters.delete(waiter);
        resolve();
      }
    });
  }

  private waitForControlDrain(timeoutMs = 1_000): Promise<void> {
    const channel = this.controlChannel;
    if (!channel || channel.readyState !== "open") {
      return Promise.reject(
        new MatchTransportError(
          "channel-closed",
          "Cannot drain rematch control messages while control is closed",
          { recoverable: true },
        ),
      );
    }
    if (channel.bufferedAmount === 0) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      const interval = setInterval(() => {
        if (channel.readyState !== "open") {
          clearInterval(interval);
          clearTimeout(timeout);
          reject(
            new MatchTransportError(
              "channel-closed",
              "Control closed before rematch messages drained",
              { recoverable: true },
            ),
          );
        } else if (channel.bufferedAmount === 0) {
          clearInterval(interval);
          clearTimeout(timeout);
          resolve();
        }
      }, 10);
      const timeout = setTimeout(() => {
        clearInterval(interval);
        reject(
          new MatchTransportError(
            "channel-closed",
            "Timed out draining rematch control messages",
            { recoverable: true },
          ),
        );
      }, timeoutMs);
    });
  }

  private rematchDeliveryGraceMs(): number {
    const estimate = this.clockEstimate;
    if (!estimate) return 150;
    return Math.min(
      500,
      Math.max(75, Math.ceil(estimate.medianRoundTripMs + estimate.jitterMs * 2)),
    );
  }

  private setState(state: PeerMatchState): void {
    if (state === this.currentState) return;
    const previous = this.currentState;
    this.currentState = state;
    this.callbacks.onStateChange?.(state, previous);
  }

  private reportProtocolError(error: unknown): void {
    const normalized = normalizeError(error);
    this.callbacks.onProtocolError?.(normalized);
    this.callbacks.onError?.(normalized);
  }

  private reportError(error: unknown, recoverable = true): void {
    const normalized = normalizeError(error);
    this.callbacks.onError?.(normalized);
    if (!recoverable || (normalized instanceof MatchTransportError && !normalized.recoverable)) {
      this.setState("aborted");
    }
  }

  private stopTimers(): void {
    this.stopClockTimer();
    if (this.heartbeatTimer !== undefined) clearInterval(this.heartbeatTimer);
    if (this.startTimer !== undefined) clearTimeout(this.startTimer);
    this.heartbeatTimer = undefined;
    this.startTimer = undefined;
  }

  private closePeerTransport(): void {
    this.controlChannel?.close();
    this.poseChannel?.close();
    this.peerConnection?.close();
  }

  /**
   * Rematch-safe close: first confirms the mutual rematch request was received,
   * then waits until signaling confirms that the Worker released this seat.
   */
  async closeAndWait(
    intent: "exit" | "rematch" = "exit",
  ): Promise<SignalingResumeReservation | undefined> {
    if (this.isClosed) return;
    this.isClosed = true;
    this.stopTimers();
    let acknowledgementError: Error | undefined;
    if (this.lastRematchRequestSeq !== undefined) {
      try {
        await this.waitForControlAcknowledgement(this.lastRematchRequestSeq);
        await this.waitForControlDrain();
        await new Promise<void>((resolve) => {
          setTimeout(resolve, this.rematchDeliveryGraceMs());
        });
      } catch (error) {
        acknowledgementError = normalizeError(error);
      }
    }
    let reservation: SignalingResumeReservation | undefined;
    try {
      reservation = await this.signaling.leaveAndWait({ intent });
    } finally {
      this.closePeerTransport();
      this.rejectControlAckWaiters(
        new MatchTransportError("channel-closed", "Session closed", { recoverable: true }),
      );
      this.setState("closed");
    }
    if (acknowledgementError) throw acknowledgementError;
    return reservation;
  }

  close(): void {
    if (this.isClosed) return;
    this.isClosed = true;
    this.stopTimers();
    this.closePeerTransport();
    this.rejectControlAckWaiters(
      new MatchTransportError("channel-closed", "Session closed", { recoverable: true }),
    );
    this.signaling.leave();
    this.setState("closed");
  }
}

/** Useful for feature gates before presenting the online-room UI. */
export function supportsPeerMatch(): boolean {
  return (
    typeof globalThis.RTCPeerConnection !== "undefined" &&
    typeof globalThis.WebSocket !== "undefined" &&
    Boolean(globalThis.crypto?.subtle) &&
    typeof globalThis.performance?.now === "function" &&
    PROTOCOL_VERSION === 1
  );
}
